import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useState } from 'react'

import { useIsMobile } from 'lib/hooks/useIsMobile'
import { useTranslation } from 'lib/i18n'
import { CallToActionStoryblok } from 'lib/storyblok/types'
import { cn } from 'lib/utils/tailwind'

import { Icon, IconOptions } from './Icon'

export interface AccordionItem {
  title: string
  content: React.ReactNode
  ctas?: CallToActionStoryblok[]
  rightElement?: React.ReactNode
  disabled?: boolean
}

interface AccordionProps {
  items: AccordionItem[]
  variant?: 'default' | 'form' | 'announcement'
  activeIndex?: number
  setActiveIndex?: (index: number) => void
  titleIcon?: IconOptions
}

export const Accordion = ({
  items,
  variant = 'default',
  activeIndex,
  setActiveIndex,
  titleIcon,
}: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(activeIndex ?? null)

  const { i18n } = useTranslation()
  const isMobileDevice = useIsMobile()

  const handleClick = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
    setActiveIndex?.(index)
  }

  useEffect(() => {
    setOpenIndex(activeIndex ?? null)
  }, [activeIndex])

  return (
    <div
      className={cn(
        'space-y-4',
        variant === 'form' && 'space-y-5',
        variant === 'announcement' && 'lg:bg-background-secondary-news-light'
      )}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className={cn(
            'rounded-5 border border-foreground/5 bg-foreground/5',
            variant === 'form' && 'bg-background',
            variant === 'announcement' &&
              'lg:max-w-screen-desktop lg:mx-auto  px-3 lg:px-5 py-3 bg-background-secondary-news-light rounded-t-none rounded-b-3 lg:rounded-b-5 border-none'
          )}
        >
          <button
            type="button"
            id={`accordion-button-${index}`}
            onClick={() => handleClick(index)}
            aria-expanded={openIndex === index}
            aria-controls={`accordion-content-${index}`}
            className={cn(
              'w-full p-5 text-left flex justify-between items-center border-none',
              variant === 'form' && 'lg:px-8',
              variant === 'announcement' && 'p-0',
              item.disabled && 'opacity-50 pointer-events-none'
            )}
          >
            <span
              className={cn(
                'text-foreground text-twenty font-regular mr-4',
                variant === 'form' && 'text-sixteen',
                variant === 'announcement' &&
                  'text-fourteen lg:text-eighteen flex items-center gap-1.5'
              )}
            >
              {variant === 'announcement' && titleIcon && (
                <Icon
                  icon={titleIcon}
                  size={isMobileDevice ? 18 : 20}
                  className="text-foreground/60"
                  aria-hidden="true"
                />
              )}
              {item.title}
            </span>

            {item.rightElement ? (
              item.rightElement
            ) : (
              <div className="flex items-center gap-1">
                {variant === 'announcement' && (
                  <p className="hidden lg:block font-regular">
                    {openIndex === index ? i18n('showLess') : i18n('showMore')}
                  </p>
                )}
                <Icon
                  size={variant === 'announcement' ? 18 : 24}
                  aria-hidden="true"
                  icon="caret-down"
                  className={cn(
                    'text-foreground/80 transition-all duration-200 ease-in-out',
                    openIndex === index && '-rotate-180'
                  )}
                />
              </div>
            )}
          </button>

          <AnimatePresence initial={false}>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
                id={`accordion-content-${index}`}
                role="region"
                aria-labelledby={`accordion-button-${index}`}
              >
                <div
                  className={cn(
                    'px-5 pb-5 text-twenty text-foreground font-light',
                    variant === 'form' &&
                      'p-5 lg:p-8 border-t border-foreground/10',
                    variant === 'announcement' &&
                      'text-fourteen lg:text-sixteen p-0 mt-1 lg:mb-2'
                  )}
                >
                  {item.content}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  )
}
